import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Building2, Users, Briefcase, TrendingUp } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Partner = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const industries = [
    "Timeshare/Resort",
    "Hospitality",
    "Real Estate",
    "Travel & Tourism",
    "Retail",
    "Healthcare",
    "Technology",
    "Other"
  ];

  const benefits = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Data-Driven Results",
      description: "We use advanced analytics to optimize campaigns and deliver measurable ROI."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Dedicated Team",
      description: "Your own team of marketing experts committed to your success."
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Industry Expertise",
      description: "Deep experience in timeshare, hospitality, and lead generation."
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Full-Service Solutions",
      description: "From OPC marketing to AI tools, we cover all your marketing needs."
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIndustrySelect = (industry) => {
    setFormData(prev => ({ ...prev, industry }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.company || !formData.industry || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/partner`, formData);
      if (response.data.success) {
        toast.success('Thank you! Our team will contact you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          industry: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting partner form:', error);
      toast.error('Failed to submit. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="partner-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-[#EA580C] to-[#DC2626]">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-white/20 text-white text-xs font-bold uppercase tracking-wider rounded-full mb-6">
            Partnership Opportunity
          </span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-6 max-w-3xl mx-auto">
            Become a Partner & Dominate Your Market
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Join hundreds of successful businesses who've partnered with Prestigious Marketing 
            to achieve extraordinary results.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Why Partner With Us
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              The Prestigious Advantage
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <Card key={idx} className="border-slate-200 card-hover">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-[#1E3A8A] text-white rounded-2xl flex items-center justify-center mx-auto mb-5">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding bg-slate-50" data-testid="partner-form-section">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
                Get Started
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Request a Partnership
              </h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and our partnership team will reach out within 24 hours 
                to discuss how we can help grow your business.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="partner-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 font-medium">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="mt-1.5 bg-white"
                      data-testid="partner-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className="mt-1.5 bg-white"
                      data-testid="partner-email-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 font-medium">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="mt-1.5 bg-white"
                      data-testid="partner-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-slate-700 font-medium">Company *</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company Name"
                      className="mt-1.5 bg-white"
                      data-testid="partner-company-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-700 font-medium mb-3 block">Industry *</Label>
                  <div className="flex flex-wrap gap-2">
                    {industries.map((industry) => (
                      <button
                        key={industry}
                        type="button"
                        onClick={() => handleIndustrySelect(industry)}
                        className={`industry-badge ${formData.industry === industry ? 'selected' : ''}`}
                        data-testid={`industry-${industry.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      >
                        {industry}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-700 font-medium">Tell us about your goals *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="What are you looking to achieve? What services are you interested in?"
                    className="mt-1.5 bg-white min-h-[120px]"
                    data-testid="partner-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-[#EA580C] hover:bg-[#c2410c] text-white"
                  disabled={isSubmitting}
                  data-testid="partner-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Partnership Request'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>

            {/* Info */}
            <div className="lg:pl-8">
              <div className="bg-[#0F172A] text-white rounded-2xl p-8 lg:p-10 mb-8">
                <h3 className="text-xl font-bold mb-6">What Happens Next?</h3>
                <ul className="space-y-4">
                  {[
                    "Our partnership team reviews your submission",
                    "We schedule a discovery call within 24 hours",
                    "We create a custom strategy for your business",
                    "Launch your marketing campaign"
                  ].map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#EA580C] flex items-center justify-center flex-shrink-0 text-sm font-bold">
                        {idx + 1}
                      </div>
                      <span className="text-slate-300">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Partnership Inquiries</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    For direct partnership inquiries, contact:
                  </p>
                  <a 
                    href="mailto:info@prestigious-marketing.com" 
                    className="text-[#1E3A8A] font-medium hover:underline"
                  >
                    info@prestigious-marketing.com
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
            Trusted by Industry Leaders
          </h2>
          <p className="text-slate-600 mb-12 max-w-2xl mx-auto">
            We've helped hundreds of businesses in the timeshare, hospitality, and travel 
            industries achieve their marketing goals.
          </p>
          <div className="flex flex-wrap justify-center gap-8 items-center opacity-60">
            {/* Placeholder for partner logos */}
            {[1, 2, 3, 4, 5].map((_, idx) => (
              <div key={idx} className="w-32 h-12 bg-slate-200 rounded-lg" />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partner;
